"use client";

import type { Interpolation } from "@emotion/react";
import { Link, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { Community, CommunityType } from "@packages/sdk";
import {
  getCommunityMemberCount,
  useActiveSidebarTab,
  useRequestCommunitiesChildren,
} from "@packages/sdk";
import type { CSSColorStrict } from "@packages/ui";
import {
  AvatarCommunity,
  Carousel,
  cssMediaQueryMinWidthTablet,
  spacing,
  useCarousel,
} from "@packages/ui";
import { useEffect } from "react";

import { getBasePADUrl, padTabsPaths } from "../../../../lib";

const LOADING_SKELETONS_TOTAL = 8;

const carouselStyles: Interpolation = {
  paddingBottom: spacing.m,
  paddingTop: spacing.m,
  maxWidth: "100%",
  [cssMediaQueryMinWidthTablet]: {
    maxWidth: `calc(100vw - ${spacing.sidebar})`,
  },
};

type CommunitiesCarouselProps = {
  parentCommunity: Pick<Community, "id" | "type">;
  type: CommunityType;
};

export const CommunitiesCarousel = ({
  parentCommunity,
  type,
}: CommunitiesCarouselProps) => {
  const t = useTranslations();
  const analytics = useAnalytics();

  const activeSidebarTab = useActiveSidebarTab({
    baseUrl: getBasePADUrl({ communityId: parentCommunity.id }),
    tabs: padTabsPaths,
  });

  const carouselApi = useCarousel({
    pageOffset: [
      [24, 32],
      [24, 24],
    ],
    size: [2, 2, 3, 4, 5, 6],
  });

  const { infiniteQuery: communitiesChildrenSmallGroupsInfiniteQuery } =
    useRequestCommunitiesChildren({
      id: parentCommunity.id,
      type: type,
    });

  const shouldFetchNextPageCommhnitiesChildrenSmallGroupsInfiniteQuery =
    carouselApi.endIsVisible &&
    communitiesChildrenSmallGroupsInfiniteQuery.hasNextPage &&
    !communitiesChildrenSmallGroupsInfiniteQuery.isFetchingNextPage;

  useEffect(() => {
    if (shouldFetchNextPageCommhnitiesChildrenSmallGroupsInfiniteQuery)
      communitiesChildrenSmallGroupsInfiniteQuery.fetchNextPage();
  }, [shouldFetchNextPageCommhnitiesChildrenSmallGroupsInfiniteQuery]);

  const childCommunities = (
    communitiesChildrenSmallGroupsInfiniteQuery.data?.pages ?? []
  ).flatMap((page) => page.results);

  return (
    <Carousel carouselApi={carouselApi} containerCSS={[carouselStyles]}>
      {!communitiesChildrenSmallGroupsInfiniteQuery.data
        ? [...Array(LOADING_SKELETONS_TOTAL)].map((index) => (
            <AvatarCommunity key={index} loading size="responsive" />
          ))
        : [
            ...(childCommunities ?? []).map((community) => {
              return (
                <Link
                  href={`/admin/${parentCommunity.id}/dashboard/community/${community.id}`}
                  key={`communityCarouselItem-${community.id}`}
                  onClick={() =>
                    analytics.track({
                      event: HallowAnalyticsEvent.TappedGroup,
                      properties: {
                        parent_group_type: parentCommunity.type,
                        community_id: community.id,
                        location: activeSidebarTab
                          ? `${activeSidebarTab}_tab`
                          : "",
                        group_type: type,
                      },
                    })
                  }
                >
                  <AvatarCommunity
                    color={community.color_hex as CSSColorStrict}
                    name={community.name}
                    imgSrc={community.image_url}
                    size="responsive"
                    topLeftImgSrc={community.member_summary.image_urls?.[0]}
                    bottomRightImgSrc={community.member_summary.image_urls?.[1]}
                    sublabel={t("plural_members", {
                      plural_members: getCommunityMemberCount({ community }),
                    })}
                  />
                </Link>
              );
            }),
            ...(communitiesChildrenSmallGroupsInfiniteQuery.hasNextPage
              ? [<AvatarCommunity loading={true} size="responsive" />]
              : []),
          ]}
    </Carousel>
  );
};
